import { useEffect, useLayoutEffect, useState } from 'react';
import {
  resetDropdownDomStyles,
  updateDropdownDomStyles as _updateDropdownDomStyles,
} from './updateDropdownDomStyles';
import { useDidUpdate } from '../../../../../../utils/hooks/useDidUpdate';
import { useAnimationState } from './useAnimationState';
import type {
  DropdownAnimationNameType,
  MenuAnimationNameType,
} from '../../../constants';
import {
  ANIMATION_PACKAGE_CONFIG,
  DROPDOWN_ANIMATION_CONFIG,
  INITIAL_DROPDOWN_ANIMATION_STATE,
} from '../../../animations';

type UseDropdownProps = {
  itemRef: React.MutableRefObject<HTMLLIElement | null>;
  forceOpen: boolean;
  getAnimationPackage: () => MenuAnimationNameType;
};

export const useDropdown = ({
  itemRef,
  forceOpen,
  getAnimationPackage,
}: UseDropdownProps) => {
  const updateDropdownDomStyles = () =>
    _updateDropdownDomStyles(itemRef.current);

  const {
    animationState,
    initEnterAnimation,
    initExitAnimation,
    skipToEnterDone,
    skipToExitDone,
  } = useAnimationState(
    DROPDOWN_ANIMATION_CONFIG,
    INITIAL_DROPDOWN_ANIMATION_STATE,
  );
  const [isOpen, setIsOpen] = useState(forceOpen);

  const getDropdownAnimationName = (): DropdownAnimationNameType => {
    const animationPackage = getAnimationPackage();
    const dropdownAnimationName =
      ANIMATION_PACKAGE_CONFIG[animationPackage].dropdownAnimationName;

    return dropdownAnimationName;
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    updateDropdownDomStyles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const showDropdown = () => {
    const dropdownAnimationName = getDropdownAnimationName();

    initEnterAnimation(dropdownAnimationName);
    setIsOpen(true);
    itemRef.current?.focus();
  };

  const showDropdownImmediate = () => {
    const dropdownAnimationName = getDropdownAnimationName();
    skipToEnterDone(dropdownAnimationName);
    setIsOpen(true);
  };

  const hideDropdown = () => {
    if (!isOpen) {
      return;
    }
    const dropdownAnimationName = getDropdownAnimationName();
    initExitAnimation(dropdownAnimationName, () => setIsOpen(false));
  };

  const hideDropdownImmediate = () => {
    if (!isOpen) {
      return;
    }
    const dropdownAnimationName = getDropdownAnimationName();
    skipToExitDone(dropdownAnimationName);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (isOpen) {
      hideDropdown();
    } else {
      showDropdown();
    }
  };

  useDidUpdate(() => {
    if (!isOpen) {
      resetDropdownDomStyles(itemRef.current!);
    }
  }, [isOpen]);

  useDidUpdate(() => {
    if (forceOpen) {
      showDropdown();
    } else {
      hideDropdown();
    }
  }, [forceOpen]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => resetDropdownDomStyles(itemRef.current!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isOpen,
    dropdownAnimationState: animationState,
    showDropdown,
    showDropdownImmediate,
    hideDropdown,
    hideDropdownImmediate,
    toggleDropdown,
    updateDropdownDomStyles,
  };
};
